<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppHeader from "@/components/AppHeader.vue";
import MobilePrimaryNav from "@/components/navigation/MobilePrimaryNav.vue";
import AuthDialog from "@/components/AuthDialog.vue";
import ShareDialog from "@/components/ShareDialog.vue";
import MapAgentWidget from "@/components/MapAgentWidget.vue";
import InitialHistoryLoading from "@/components/InitialHistoryLoading.vue";
import InitialWelcomeGuide from "@/components/InitialWelcomeGuide.vue";
import HistoryScrollViewer from "@/components/HistoryScrollViewer.vue";
import { useAuth } from "@/composables/useAuth";
import { mapNebulaMode } from "@/composables/useMapNebulaMode";
import type { AgentCard, AgentMapAction, AgentMapContext } from "@/services/api/agentApi";
import { provide } from "vue";
import { AgentCapabilityRegistry } from "@/agent/AgentCapabilityRegistry";
import { routeForAgentCommand } from "@/agent/AgentCommandRoute";
import {
  locationForPageCommand,
  pageCommandMissingTargetMessage
} from "@/agent/AgentPageCommandNavigation";
import { AgentCommandQueue } from "@/agent/AgentCommandQueue";
import { AgentCommandExecutor } from "@/agent/AgentCommandExecutor";
import { BackendAgentCommandReporter } from "@/agent/AgentCommandReporter";
import { BackendAgentV4CommandReporter } from "@/agent/AgentCommandReporter";
import { AgentWorldStateBuilder } from "@/agent/AgentWorldStateBuilder";
import { getOrCreateSessionId, resetSessionId } from "@/agent/AgentSessionStore";
import {
  fetchAgentFinalAnswer,
  sendAgentV2Message,
  sendAgentV2MessageStream,
  type StreamedAgentStep
} from "@/services/api/agentV2Api";
import { assertAgentV4CapabilityContract, fetchAgentV4FinalAnswer, sendAgentV4MessageStream } from "@/services/api/agentV4Api";
import { AGENT_EXECUTOR_KEY, AGENT_REGISTRY_KEY } from "@/agent/agentContext";
import type { MainNavPage } from "@/navigation/mainNavigation";
import { preloadKgOverviewGraph } from "@/services/api/kgApi";
import type {
  AgentUiCommand,
  AgentV2ChatResponse,
  CommandProgressItem,
  CommandReceipt,
  QueueEvent,
  V4ProcessStep
} from "@/agent/types";
import { shouldApplyReconciledAnswer } from "@/agent/FinalAnswerReconciler";
import {
  createTurnFinalAnswer,
  mergeTurnFinalAnswer,
  type TurnFinalAnswer
} from "@/agent/TurnFinalAnswer";

const agentRegistry = new AgentCapabilityRegistry();
provide(AGENT_REGISTRY_KEY, agentRegistry);

const useV4UiShadow = import.meta.env.VITE_AGENT_V4_UI_SHADOW === "true";

// Agent 2.0 命令执行器（单一执行源；页面能力经 registry 注册）
const agentCommandQueue = new AgentCommandQueue();
const agentReporter = useV4UiShadow
  ? new BackendAgentV4CommandReporter()
  : new BackendAgentCommandReporter();
const agentWorldStateBuilder = new AgentWorldStateBuilder(agentRegistry);
const agentExecutor = new AgentCommandExecutor({
  registry: agentRegistry,
  queue: agentCommandQueue,
  reporter: agentReporter,
  dispatch: dispatchAgentCommand,
  onTurnFinished: (info) => {
    if (cancelledAgentTurns.has(info.turnId) || activeAgentTurnId.value !== info.turnId) {
      return;
    }
    agentExecuting.value = false;
    if (info.answerPatch) {
      // /commands/result already returns the server-composed final answer.
      // Render it immediately; the following /answer fetch is reconciliation,
      // not the sole delivery path.  This prevents a completed research answer
      // from being lost when the follow-up fetch is interrupted by navigation.
      commitAgentFinalAnswer(info.turnId, info.answerPatch, info.planStatus);
      agentCommandProgress.value = [
        ...agentCommandProgress.value,
        {
          commandId: "plan",
          commandType: "answer",
          status: "patched",
          summary: info.answerPatch
        }
      ];
    }
    if (info.sessionId && info.turnId && info.planId) {
      void loadAgentFinalAnswer(info.sessionId, info.turnId, info.planId, {
        answer: info.answerPatch,
        planStatus: info.planStatus
      });
    }
  }
});
provide(AGENT_EXECUTOR_KEY, agentExecutor);
agentExecutor.onEvent(handleExecutorEvent);

interface CityNebulaExpose {
  resetNebula: () => void;
  focusByAgent: (payload: { buildingId?: string; buildingName?: string; query?: string }) => Promise<void>;
  buildAgentContext: () => AgentMapContext;
}

interface HeritageMapExpose {
  showAll: () => void;
  navigateHome: () => void;
  buildAgentContext: () => AgentMapContext;
  clearAgentTransientState: () => void;
  applyAgentMapActions: (actions: AgentMapAction[]) => Promise<void>;
}

interface MapAgentWidgetExpose {
  open: (message?: string) => void;
}

interface AgentMemory {
  lastBuildingId: string;
  lastBuildingName: string;
  contextStrong: boolean;
  recentMessages: Array<{ role: "user" | "assistant"; content: string }>;
}

const route = useRoute();
const router = useRouter();
const hasVisitedNebula = ref(false);
watch(
  () => route.name,
  (name) => {
    if (name === "nebula") hasVisitedNebula.value = true;
  },
  { immediate: true }
);
const isGrowthRoute = computed(
  () => route.name === "growth" || route.name === "growth-s3m-test"
);
const INITIAL_WELCOME_GUIDE_KEY = "jiangcheng-memory:welcome-guide-seen";
const INITIAL_HISTORY_LOADING_KEY = "jiangcheng-memory:history-scroll-seen";
const INITIAL_HISTORY_LOADING_DURATION = 2800;

function shouldShowInitialWelcome(): boolean {
  try {
    return window.localStorage.getItem(INITIAL_WELCOME_GUIDE_KEY) !== "1";
  } catch {
    // 存储不可用时优先保证新用户仍能看到引导。
    return true;
  }
}

function shouldPlayInitialHistoryLoading(): boolean {
  try {
    const navigation = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (navigation?.type === "reload") {
      window.sessionStorage.removeItem(INITIAL_HISTORY_LOADING_KEY);
    }
    return window.sessionStorage.getItem(INITIAL_HISTORY_LOADING_KEY) !== "1";
  } catch {
    return true;
  }
}

// 首次访问展示欢迎引导；已看过引导的后续打开恢复原有卷轴加载页。
// /welcome 是一个显式的演示入口，不能受“已经看过引导”的本地标记影响。
const initialWelcomeVisible = ref(route.name === "welcome" || shouldShowInitialWelcome());
const initialHistoryLoadingVisible = ref(
  !initialWelcomeVisible.value && shouldPlayInitialHistoryLoading()
);
const initialHistoryClosing = ref(false);
const historyScrollViewerOpen = ref(false);
const historyScrollEntryAttention = ref(false);
let historyScrollAttentionTimer: number | undefined;
const initialHistoryImageResolved = ref(!initialHistoryLoadingVisible.value);
const initialPageReady = ref(false);
const initialMapReady = ref(false);
const initialNebulaReady = ref(false);
const initialHistoryMinimumElapsed = ref(!initialHistoryLoadingVisible.value);
// 欢迎页遮罩显示期间预挂载默认的星云主页面。这样图谱请求、异步分包和 WebGL
// 初始化都会在用户阅读欢迎页时完成，而不是点击“跳过引导”后才开始。
const welcomeMainPreloadReady = ref(false);
let initialHistoryDurationTimer: number | undefined;
let initialHistoryFallbackTimer: number | undefined;
let welcomeGraphPreloadStarted = false;
let welcomeGraphPreloadTimer: number | undefined;
let welcomeGraphPreloadIdleId: number | undefined;
let welcomeMainPreloadTimer: number | undefined;

type WelcomeIdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

function startWelcomeGraphPreload(): void {
  if (welcomeGraphPreloadStarted) return;
  welcomeGraphPreloadStarted = true;
  if (welcomeGraphPreloadTimer !== undefined) {
    window.clearTimeout(welcomeGraphPreloadTimer);
    welcomeGraphPreloadTimer = undefined;
  }
  const idleWindow = window as WelcomeIdleWindow;
  if (welcomeGraphPreloadIdleId !== undefined) {
    idleWindow.cancelIdleCallback?.(welcomeGraphPreloadIdleId);
    welcomeGraphPreloadIdleId = undefined;
  }
  void preloadKgOverviewGraph().catch((error) => {
    console.warn("[Welcome] Background graph preload failed:", error);
  });
}

function scheduleWelcomeGraphPreload(): void {
  if (welcomeGraphPreloadStarted || !initialWelcomeVisible.value) return;
  const idleWindow = window as WelcomeIdleWindow;
  if (idleWindow.requestIdleCallback) {
    welcomeGraphPreloadIdleId = idleWindow.requestIdleCallback(
      () => {
        welcomeGraphPreloadIdleId = undefined;
        startWelcomeGraphPreload();
      },
      { timeout: 1200 }
    );
    return;
  }
  welcomeGraphPreloadTimer = window.setTimeout(startWelcomeGraphPreload, 600);
}

function startWelcomeMainPreload(): void {
  if (!initialWelcomeVisible.value || welcomeMainPreloadReady.value) return;
  if (welcomeMainPreloadTimer !== undefined) {
    window.clearTimeout(welcomeMainPreloadTimer);
    welcomeMainPreloadTimer = undefined;
  }
  welcomeMainPreloadReady.value = true;
  startWelcomeGraphPreload();
}

function scheduleWelcomeMainPreload(): void {
  if (!initialWelcomeVisible.value || welcomeMainPreloadReady.value || welcomeMainPreloadTimer !== undefined) {
    return;
  }
  // 先让欢迎页的首帧、字体与首屏海报稳定，再在其后的短暂窗口启动主页面。
  welcomeMainPreloadTimer = window.setTimeout(startWelcomeMainPreload, 550);
}

function finishInitialHistoryLoading(): void {
  if (
    !initialHistoryLoadingVisible.value ||
    initialHistoryClosing.value ||
    !initialPageReady.value ||
    !initialHistoryImageResolved.value ||
    !initialHistoryMinimumElapsed.value ||
    (route.path === "/" && !initialNebulaReady.value) ||
    // The map view is mounted behind the landing page and loads its points in
    // parallel. Keep the initial scroll loader up until both hidden and active
    // views are ready so switching to the map never starts the first fetch.
    (route.path === "/" && !initialMapReady.value) ||
    (route.path === "/map" && !initialMapReady.value)
  ) {
    return;
  }
  try {
    window.sessionStorage.setItem(INITIAL_HISTORY_LOADING_KEY, "1");
  } catch {
    // 无痕模式或禁用本地存储时仍允许正常进入页面。
  }
  initialHistoryClosing.value = true;
}

function handleInitialHistoryClosed(): void {
  initialHistoryLoadingVisible.value = false;
  historyScrollEntryAttention.value = true;
  if (historyScrollAttentionTimer !== undefined) window.clearTimeout(historyScrollAttentionTimer);
  historyScrollAttentionTimer = window.setTimeout(() => {
    historyScrollEntryAttention.value = false;
    historyScrollAttentionTimer = undefined;
  }, 1800);
}

function startInitialHistoryMinimumDuration(): void {
  if (!initialHistoryLoadingVisible.value || initialHistoryDurationTimer !== undefined) return;
  initialHistoryDurationTimer = window.setTimeout(() => {
    initialHistoryMinimumElapsed.value = true;
    finishInitialHistoryLoading();
  }, INITIAL_HISTORY_LOADING_DURATION);
}

function removeInitialHistoryBootLoader(): void {
  document.getElementById("initial-history-boot")?.remove();
}

function resolveInitialHistoryImage(): void {
  if (initialHistoryFallbackTimer !== undefined) {
    window.clearTimeout(initialHistoryFallbackTimer);
    initialHistoryFallbackTimer = undefined;
  }
  removeInitialHistoryBootLoader();
  initialHistoryImageResolved.value = true;
  startInitialHistoryMinimumDuration();
  finishInitialHistoryLoading();
}

function handleInitialMapReady(): void {
  initialMapReady.value = true;
  finishInitialHistoryLoading();
}

function handleInitialNebulaReady(): void {
  initialNebulaReady.value = true;
  finishInitialHistoryLoading();
}

function closeInitialWelcome(returnToNebula = true): void {
  if (!initialWelcomeVisible.value) return;
  // 若用户很快跳过，立即启动/接管主页面预加载。
  startWelcomeMainPreload();
  try {
    window.localStorage.setItem(INITIAL_WELCOME_GUIDE_KEY, "1");
  } catch {
    // 标记写入失败不阻断当次进入网站。
  }
  initialWelcomeVisible.value = false;
  removeInitialHistoryBootLoader();
  if (returnToNebula && route.name === "welcome") {
    void router.replace({ name: "nebula" }).catch(() => undefined);
  }
}

function openGuideAgent(prompt = ""): void {
  const returnFromWelcome = route.name === "welcome";
  closeInitialWelcome(false);
  const openWidget = () => void nextTick().then(() => agentWidgetRef.value?.open(prompt));
  if (returnFromWelcome) {
    void router.replace({ name: "nebula" }).catch(() => undefined).finally(openWidget);
    return;
  }
  openWidget();
}

function handleInitialWelcomeAction(
  action: "map" | "photos" | "route" | "nebula" | "growth" | "crowdsource"
): void {
  closeInitialWelcome(false);
  if (action === "map") {
    void router.push({ name: "map", query: { from: "welcome-guide" } }).catch(() => undefined);
    return;
  }
  if (action === "photos") {
    void router.push({ name: "old-photos", query: { from: "welcome-guide" } }).catch(() => undefined);
    return;
  }
  if (action === "nebula") {
    void router.push({ name: "nebula", query: { from: "welcome-guide" } }).catch(() => undefined);
    return;
  }
  if (action === "growth") {
    void router.push({ name: "growth", query: { from: "welcome-guide" } }).catch(() => undefined);
    return;
  }
  if (action === "crowdsource") {
    void router.push({ name: "crowdsource", query: { from: "welcome-guide" } }).catch(() => undefined);
    return;
  }
  const routePrompt = "周末想去汉口走走，想看江汉关和老洋行，适合拍照，两三个小时，又不想走太远。";
  void router.push({ name: "map", query: { from: "welcome-guide", prompt: "culture-route" } })
    .catch(() => undefined)
    .finally(() => openGuideAgent(routePrompt));
}

watch(
  () => route.name,
  (name) => {
    if (name !== "welcome") return;
    initialHistoryLoadingVisible.value = false;
    initialHistoryClosing.value = false;
    initialWelcomeVisible.value = true;
    removeInitialHistoryBootLoader();
    scheduleWelcomeGraphPreload();
    scheduleWelcomeMainPreload();
  }
);

const CityNebulaView = defineAsyncComponent(() => import("@/views/CityNebulaView.vue"));
const HeritageMapView = defineAsyncComponent(() => import("@/views/HeritageMapView.vue"));
const BuildingDetailView = defineAsyncComponent(() => import("@/views/BuildingDetailView.vue"));
const MemoryGridInsightView = defineAsyncComponent(() => import("@/views/MemoryGridInsightView.vue"));
const SpatialStatsView = defineAsyncComponent(() => import("@/views/SpatialStatsView.vue"));
const OldPhotoHomeView = defineAsyncComponent(() => import("@/views/OldPhotoHomeView.vue"));
const PhotoArchiveSystemView = defineAsyncComponent(() => import("@/views/PhotoArchiveSystemView.vue"));
const PhotoWorkbenchView = defineAsyncComponent(() => import("@/views/PhotoWorkbenchView.vue"));
const MyImagesView = defineAsyncComponent(() => import("@/views/MyImagesView.vue"));
const PhotoAlignmentAdminView = defineAsyncComponent(() => import("@/views/PhotoAlignmentAdminView.vue"));
const PhotoPoseAnnotationView = defineAsyncComponent(() => import("@/views/PhotoPoseAnnotationView.vue"));
const CrowdSourceHome = defineAsyncComponent(() => import("@/views/CrowdSourceHome.vue"));
const CrowdSourceSubmitView = defineAsyncComponent(() => import("@/views/CrowdSourceSubmitView.vue"));
const ContributionBrowseView = defineAsyncComponent(() => import("@/views/ContributionBrowseView.vue"));
const CrowdMapView = defineAsyncComponent(() => import("@/views/CrowdMapView.vue"));
const ArchiveReviewView = defineAsyncComponent(() => import("@/views/ArchiveReviewView.vue"));
const ArchiveTopicWorkbenchView = defineAsyncComponent(() => import("@/views/ArchiveTopicWorkbenchView.vue"));
const ContributionDetailView = defineAsyncComponent(() => import("@/views/ContributionDetailView.vue"));
const UrbanGrowthView = defineAsyncComponent(() => import("@/views/UrbanGrowthView.vue"));
const UrbanGrowthS3MTestView = defineAsyncComponent(() => import("@/views/UrbanGrowthS3MTestView.vue"));
const nebulaViewRef = ref<CityNebulaExpose | null>(null);
const mapViewRef = ref<HeritageMapExpose | null>(null);
const agentWidgetRef = ref<MapAgentWidgetExpose | null>(null);
const auth = useAuth();
const shareOpen = ref(false);
const agentResponse = ref<AgentV2ChatResponse | null>(null);
const agentLoading = ref(false);
const agentError = ref("");
const agentCommandProgress = ref<CommandProgressItem[]>([]);
const agentFinalAnswer = ref<TurnFinalAnswer | null>(null);
const agentInterimAnswer = ref("");
const agentV4ProcessSteps = ref<V4ProcessStep[]>([]);
/** 等待回复期间流式到达的执行步骤（转发给 MapAgentWidget 实时展示，最终结果后整体替换） */
const agentLiveSteps = ref<StreamedAgentStep[]>([]);
/** 命令队列执行中（光效持续到操作全部完成） */
const agentExecuting = ref(false);
const agentMemory = ref<AgentMemory>({
  lastBuildingId: "",
  lastBuildingName: "",
  contextStrong: false,
  recentMessages: []
});
const activeAgentTurnId = ref("");
const cancelledAgentTurns = new Set<string>();
let agentController: AbortController | null = null;
let finalAnswerController: AbortController | null = null;

const activePage = computed<MainNavPage>(() => {
  if (
    String(route.name || "").startsWith("photo-") ||
    route.name === "old-photos" ||
    route.name === "archive-topic-workbench" ||
    route.name === "building-reposition" ||
    route.name === "photo-reposition"
  ) {
    return "reposition";
  }
  if (
    String(route.name || "").startsWith("crowdsource") ||
    route.name === "archive-review" ||
    route.name === "contribution-detail" ||
    route.name === "contribution-browse" ||
    route.name === "crowd-map"
  ) {
    return "crowdsource";
  }
  if (route.name === "growth" || route.name === "growth-s3m-test") {
    return "growth";
  }
  return route.path === "/map" ? "map" : "nebula";
});
const showGlobalHeader = computed(
  () =>
    route.name !== "building-detail" &&
    route.name !== "photo-alignment-admin" &&
    route.name !== "photo-pose-annotation" &&
    route.name !== "memory-grid-insight" &&
    route.name !== "spatial-stats"
);
const mobileNavExcludedRoutes = new Set([
  "crowdsource-submit",
  "photo-workbench",
  "photo-alignment-admin",
  "photo-pose-annotation",
  "archive-review",
  "archive-topic-workbench",
  "growth-s3m-test"
]);
const showMobilePrimaryNav = computed(
  () => showGlobalHeader.value && !mobileNavExcludedRoutes.has(String(route.name || ""))
);
const isOldPhotoModule = computed(() =>
  route.name === "old-photos" ||
  route.name === "photo-archive" ||
  route.name === "photo-workbench" ||
  route.name === "my-images" ||
  route.name === "photo-located" ||
  route.name === "photo-reposition" ||
  route.name === "building-reposition"
);

function handleBrandAction(): void {
  if (activePage.value === "map") {
    mapViewRef.value?.showAll();
    return;
  }
  if (activePage.value === "reposition") {
    void router.push({ name: "nebula" }).catch(() => undefined);
    return;
  }
  if (activePage.value === "crowdsource") {
    void router.push({ name: "crowdsource" }).catch(() => undefined);
    return;
  }
  if (activePage.value === "growth") {
    void router.push({ name: "growth" }).catch(() => undefined);
    return;
  }
  nebulaViewRef.value?.resetNebula();
}

async function navigateToReposition(): Promise<void> {
  await router.push({ name: "old-photos" }).catch(() => undefined);
}

function handleNavigate(page: MainNavPage): void {
  if (page === "reposition") {
    void navigateToReposition();
    return;
  }
  if (page === "crowdsource") {
    void router.push({ name: "crowdsource" }).catch(() => undefined);
    return;
  }
  if (page === "growth") {
    if (page === activePage.value) {
      handleBrandAction();
      return;
    }
    void router.push({ name: "growth" }).catch(() => undefined);
    return;
  }

  if (page === activePage.value) {
    handleBrandAction();
    return;
  }

  if (page === "nebula") {
    if (route.path === "/map") {
      mapViewRef.value?.navigateHome();
    } else {
      void router.push({ name: "nebula" }).catch(() => undefined);
    }
    return;
  }

  void router.push({
    name: "map",
    query: { from: activePage.value === "nebula" ? "nebula-overview" : "main-nav" }
  }).catch(() => undefined);
}

function buildGlobalAgentContext(): AgentMapContext {
  const routeBuildingId = typeof route.params.buildingId === "string" ? route.params.buildingId : "";
  return agentWorldStateBuilder.build({
    routeName: typeof route.name === "string" ? route.name : null,
    routePath: route.fullPath,
    routeBuildingId,
    memory: {
      lastBuildingId: agentMemory.value.lastBuildingId,
      lastBuildingName: agentMemory.value.lastBuildingName,
      recentTurns: agentMemory.value.recentMessages
    }
  });
}

const agentWelcomeContext = computed(() => buildGlobalAgentContext());

function acceptAgentResponse(content: string, result: AgentV2ChatResponse): void {
  agentError.value = "";
  agentLiveSteps.value = [];
  agentInterimAnswer.value = "";
  // The stream already contains the actual node events. Only use the final
  // state summary when no event reached the browser (for example a cached or
  // very fast response).
  if (!agentV4ProcessSteps.value.length) {
    agentV4ProcessSteps.value = result.v4ProcessSteps || [];
  }
  agentResponse.value = result;
  activeAgentTurnId.value = result.turnId;
  rememberAgentTurn(content, result);
  agentExecuting.value = true;
  agentExecutor.start(result);
}

/**
 * The child has already appended the user's message when this handler starts,
 * but Vue cannot paint it until the current click task yields.  Do that before
 * collecting map data or touching hundreds of Leaflet markers.
 */
async function presentAgentSubmitFrame(): Promise<void> {
  await nextTick();
  await new Promise<void>((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }
    window.requestAnimationFrame(() => resolve());
  });
}

/**
 * V4 的即时接话和进度只反映 LangGraph 状态阶段，不借用 V3 的工具调试文案。
 * 这组状态会在网络等待期间先占位，真实 V4 响应回来后再由后端 graph state 替换。
 */
function v4InterimAnswer(message: string): string {
  const normalized = message.replace(/\s+/g, "").toLowerCase();
  const focus = compactV4InterimFocus(message);
  if (/路线|游览|逛逛|带我看看/.test(normalized)) {
    return `好的，我先按“${focus}”梳理游览目标，再从平台已有地点中整理方案。`;
  }
  if (/你好|您好|干什么|能做什么|第一次来/.test(normalized)) {
    return /第一次来/.test(normalized)
      ? "好的，我先了解你的兴趣，再从适合第一次认识武汉的内容开始。"
      : `好的，我先根据“${focus}”介绍我能帮你探索的内容。`;
  }
  if (/打开|定位|高亮|地图|页面/.test(normalized)) {
    return `好的，我先确认“${focus}”对应的目标和当前页面，再执行操作。`;
  }
  if (/他|她|它|这里|这附近|刚才|那个|其中/.test(normalized)) {
    return `好的，我先接上上下文，确认“${focus}”这次具体指向的对象。`;
  }
  if (/为什么|为何|原因|怎么发展|如何形成/.test(normalized)) {
    return `好的，我先围绕“${focus}”梳理历史背景和原因，并核对证据。`;
  }
  if (/关系|有关|谁|哪些人物|哪些建筑/.test(normalized)) {
    return `好的，我先核对“${focus}”涉及的实体和关系，再组织回答。`;
  }
  if (/是什么|什么地方|哪里|哪座|哪个|介绍|讲讲|故事/.test(normalized)) {
    return `好的，我先查找“${focus}”对应的平台资料，再给你讲清楚。`;
  }
  return `好的，我先围绕“${focus}”梳理相关内容，再给你答复。`;
}

function compactV4InterimFocus(message: string): string {
  const cleaned = message.replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim();
  const quoted = cleaned.match(/[“「『"]([^”」』"]+)[”」』"]/);
  const value = (quoted?.[1] || cleaned).replace(/[。！？!?]+$/, "").trim();
  if (value.length <= 24) return value || "这个问题";
  return `${value.slice(0, 24)}…`;
}

async function handleAgentSubmit(message: string): Promise<void> {
  const content = message.trim();
  if (!content || agentLoading.value) return;
  handleAgentCancel(false);
  agentController?.abort();
  agentController = new AbortController();
  const requestController = agentController;
  agentLoading.value = true;
  agentError.value = "";
  agentCommandProgress.value = [];
  agentFinalAnswer.value = null;
  agentLiveSteps.value = [];
  agentV4ProcessSteps.value = [];
  agentInterimAnswer.value = useV4UiShadow ? v4InterimAnswer(content) : "";
  activeAgentTurnId.value = "";
  const context = buildGlobalAgentContext();
  const sessionId = getOrCreateSessionId();
  try {
    await presentAgentSubmitFrame();
    if (useV4UiShadow) await assertAgentV4CapabilityContract(requestController.signal);
    mapViewRef.value?.clearAgentTransientState();
    const result = useV4UiShadow
      ? await sendAgentV4MessageStream(
          { sessionId, message: content, context },
          appendV4ProcessStep,
          requestController.signal
        )
      : await sendAgentV2MessageStream(
          { sessionId, message: content, context },
          (step) => {
            agentLiveSteps.value = [...agentLiveSteps.value, step];
          },
          requestController.signal
        );
    // 最终结果携带全部执行步骤，替换流式追加的实时步骤，避免重复
    if (requestController.signal.aborted) return;
    acceptAgentResponse(content, result);
  } catch (error) {
    if (requestController.signal.aborted) return;
    // 个别反向代理会在 SSE 走 HTTP/2 时错误地中断已开始的流。普通 JSON
    // 请求不依赖长连接，作为一次性回退仍可交付同一轮回答。
    if (!useV4UiShadow && isAgentStreamTransportError(error)) {
      try {
        const result = await sendAgentV2Message(
          { sessionId, message: content, context },
          requestController.signal
        );
        if (requestController.signal.aborted) return;
        acceptAgentResponse(content, result);
        return;
      } catch (fallbackError) {
        if (requestController.signal.aborted) return;
        console.error("[Jiangxiaoyi] Fallback request failed:", fallbackError);
      }
    }
    console.error("[Jiangxiaoyi] Request failed:", error);
    agentError.value = error instanceof Error && error.message.includes("能力")
      ? error.message : "江小忆暂时没有回应，请稍后再试。";
    agentInterimAnswer.value = "";
    agentV4ProcessSteps.value = [];
  } finally {
    if (agentController === requestController) agentLoading.value = false;
  }
}

function isAgentStreamTransportError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  return error instanceof Error && /network|连接中断/i.test(error.message);
}

function appendV4ProcessStep(step: V4ProcessStep): void {
  const index = agentV4ProcessSteps.value.findIndex((item) => item.id === step.id);
  if (index < 0) {
    agentV4ProcessSteps.value = [...agentV4ProcessSteps.value, step];
    return;
  }
  const next = [...agentV4ProcessSteps.value];
  next[index] = step;
  agentV4ProcessSteps.value = next;
}

function handleAgentCancel(markProgress = true): void {
  const active = agentExecutor.getActiveTurn();
  if (active.turnId) {
    cancelledAgentTurns.add(active.turnId);
  }
  agentController?.abort();
  finalAnswerController?.abort();
  agentExecutor.cancelCurrentTurn("user_cancelled");
  agentLoading.value = false;
  agentExecuting.value = false;
  agentFinalAnswer.value = null;
  agentInterimAnswer.value = "";
  agentV4ProcessSteps.value = [];
  agentLiveSteps.value = [];
  if (markProgress) {
    agentCommandProgress.value = [
      ...agentCommandProgress.value,
      {
        commandId: `cancel-${Date.now()}`,
        commandType: "cancel",
        status: "cancelled",
        summary: "已中断本轮回答和页面操作"
      }
    ];
  }
}

function buildingIdsFromResponse(response: { cards?: AgentCard[] }): string[] {
  const ids = new Set<string>();
  for (const card of response.cards || []) {
    if (card.type === "building" && card.id) ids.add(card.id);
  }
  return [...ids];
}

function rememberAgentTurn(message: string, response: { cards?: AgentCard[]; answer?: string }): void {
  const buildingIds = buildingIdsFromResponse(response);
  agentMemory.value.contextStrong = buildingIds.length === 1;
  const nextMessages = [
    ...agentMemory.value.recentMessages,
    { role: "user" as const, content: message },
    { role: "assistant" as const, content: response.answer || "" }
  ].filter((item) => item.content.trim());
  agentMemory.value.recentMessages = nextMessages.slice(-8);
}

// ---------------------------------------------------------------- 命令派发与页面 handler

/** 把命令派发到页面能力或直接路由处理器（AppCommandHandler 等） */
async function dispatchAgentCommand(
  command: AgentUiCommand,
  signal: AbortSignal
): Promise<CommandReceipt> {
  const pageId = agentRegistry.pageIdForCommand(command);
  if (pageId) {
    const pageLocation = locationForPageCommand(command);
    if (isPageOwnedCommand(command.type) && !pageLocation) {
      return {
        status: "failed",
        errorCode: "PAGE_COMMAND_TARGET_MISSING",
        message: pageCommandMissingTargetMessage(command),
        observedState: { routeName: String(route.name || ""), commandNotExecuted: true }
      };
    }
    const commandRoute = routeForAgentCommand(command);
    if (pageLocation) {
      try {
        await router.push(pageLocation);
      } catch (error) {
        return {
          status: "failed",
          errorCode: "ROUTER_NAVIGATION_FAILED",
          message: error instanceof Error ? error.message : "目标页面跳转失败",
          observedState: { routeName: String(route.name || ""), commandNotExecuted: true }
        };
      }
    } else if (commandRoute && route.name !== commandRoute) {
      await router.push({ name: commandRoute }).catch(() => undefined);
    } else if (pageId === "nebula" && route.name !== "nebula") {
      await router.push({ name: "nebula" }).catch(() => undefined);
    } else if (pageId === "old-photos" && !isOldPhotoModule.value) {
      await router.push({ name: "old-photos" }).catch(() => undefined);
    } else if (pageId === "growth" && route.name !== "growth") {
      await router.push({ name: "growth" }).catch(() => undefined);
    }
    // Route navigation and capability mounting are asynchronous. A 1.2s
    // readiness window made otherwise valid map/nebula actions fail on a
    // cold page load. Keep it bounded by the command contract, but give data
    // heavy pages enough time to register and report ready.
    const readinessTimeout = Math.max(1200, Math.min(
      command.timeoutMs || 8000,
      pageId === "growth" ? 15000
        : pageId === "building-detail" || pageId === "memory-grid-insight" ? 15000
        : pageId === "old-photos" || pageId === "map" || pageId === "nebula" ? 12000
          : 8000
    ));
    const capability =
      agentRegistry.isReady(pageId)
        ? agentRegistry.get(pageId)
        : await agentRegistry.whenReady(pageId, readinessTimeout);
    if (capability) {
      return capability.execute(command, signal);
    }
    if (isPageOwnedCommand(command.type)) {
      return {
        status: "failed",
        errorCode: "PAGE_CAPABILITY_NOT_READY",
        message: `目标页面已导航，但页面能力 ${pageId} 没有在限定时间内就绪`,
        observedState: {
          routeName: String(route.name || ""),
          routePath: route.fullPath,
          capabilityRegistered: Boolean(agentRegistry.get(pageId)),
          pageReady: agentRegistry.isReady(pageId)
        }
      };
    }
  }
  switch (command.type) {
    case "app.navigate":
      return handleAppNavigate(command);
    default:
      return {
        status: "skipped",
        errorCode: "UNSUPPORTED_COMMAND",
        message: `不支持的命令类型：${command.type}`
      };
  }
}

function handleAgentReset(): void {
  handleAgentCancel(false);
  resetSessionId();
  agentMemory.value = { lastBuildingId: "", lastBuildingName: "", contextStrong: false, recentMessages: [] };
  agentResponse.value = null;
  agentError.value = "";
  agentCommandProgress.value = [];
  agentFinalAnswer.value = null;
  agentInterimAnswer.value = "";
  activeAgentTurnId.value = "";
}

function isPageOwnedCommand(type: string): boolean {
  return type.startsWith("archive.")
    || type.startsWith("contribution.")
    || type.startsWith("analysis.");
}


function handleAppNavigate(command: AgentUiCommand): Promise<CommandReceipt> {
  const routeName = String(command.target || command.params?.routeName || "map");
  const path = typeof command.params?.path === "string" && command.params.path ? command.params.path : "";
  const startedAt = Date.now();
  const push = path
    ? router.push(path)
    : router.push({ name: routeName });
  return push
    .then(async () => {
      await nextTick();
      const actualRouteName = String(route.name || "");
      const reached = actualRouteName === routeName;
      return {
        status: reached ? "success" as const : "failed" as const,
        errorCode: reached ? undefined : "ROUTER_TARGET_MISMATCH",
        message: reached ? "" : `页面实际停留在 ${actualRouteName || "未知页面"}`,
        observedState: { routeName: actualRouteName, routePath: route.fullPath },
        durationMs: Date.now() - startedAt
      };
    })
    .catch((error) => ({
      status: "failed" as const,
      errorCode: "ROUTER_NAVIGATION_FAILED",
      message: error instanceof Error ? error.message : "页面跳转失败",
      observedState: { routeName: String(route.name || ""), routePath: route.fullPath },
      durationMs: Date.now() - startedAt
    }));
}

/** 命令执行完成后拉取 LLM 生成的最终富回答（总结 + 城市文化讲解） */
async function loadAgentFinalAnswer(
  sessionId: string,
  turnId: string,
  planId: string,
  authority: { answer?: string; planStatus?: string } = {}
): Promise<void> {
  if (cancelledAgentTurns.has(turnId) || activeAgentTurnId.value !== turnId) {
    return;
  }
  finalAnswerController?.abort();
  finalAnswerController = new AbortController();
  try {
    const result = useV4UiShadow
      ? await fetchAgentV4FinalAnswer({ sessionId, turnId, planId }, finalAnswerController.signal)
      : await fetchAgentFinalAnswer({ sessionId, turnId, planId }, finalAnswerController.signal);
    if (shouldApplyReconciledAnswer(authority, result.answer)
        && !cancelledAgentTurns.has(turnId) && activeAgentTurnId.value === turnId) {
      commitAgentFinalAnswer(turnId, result.answer, result.planStatus);
    }
    const v4FollowUps = "followUpQuestions" in result
      ? (result.followUpQuestions || result.suggestedActions || [])
      : [];
    if (activeAgentTurnId.value === turnId && agentResponse.value && v4FollowUps.length) {
      const followUps = v4FollowUps;
      agentResponse.value = {
        ...agentResponse.value,
        suggestedActions: followUps
      };
    }
    const v4ProcessSteps = "processSteps" in result ? result.processSteps || [] : [];
    if (useV4UiShadow && activeAgentTurnId.value === turnId
        && !agentV4ProcessSteps.value.length && v4ProcessSteps.length) {
      agentV4ProcessSteps.value = v4ProcessSteps;
    }
  } catch {
    // 富回答生成失败时保留过渡回答
  }
}

function commitAgentFinalAnswer(turnId: string, answer: string, planStatus: string): boolean {
  const committed = createTurnFinalAnswer(activeAgentTurnId.value, turnId, answer, planStatus);
  if (!committed) return false;
  agentFinalAnswer.value = committed;
  agentResponse.value = mergeTurnFinalAnswer(agentResponse.value, committed);
  // The initial V4 response may be a brief placeholder while page commands
  // are pending. Replace that assistant turn in the client-side context too,
  // so the next TaskFrame sees the same answer the user saw rather than
  // "页面动作已准备" / "等待前端执行".
  const recentMessages = [...agentMemory.value.recentMessages];
  let lastUserIndex = -1;
  for (let index = recentMessages.length - 1; index >= 0; index -= 1) {
    if (recentMessages[index].role === "user") {
      lastUserIndex = index;
      break;
    }
  }
  let assistantIndex = -1;
  for (let index = lastUserIndex + 1; index < recentMessages.length; index += 1) {
    if (recentMessages[index].role === "assistant") {
      assistantIndex = index;
      break;
    }
  }
  if (assistantIndex >= 0) {
    recentMessages[assistantIndex] = { ...recentMessages[assistantIndex], content: answer };
  } else if (lastUserIndex >= 0 && answer.trim()) {
    // A fast/empty V4 placeholder may have been filtered out by
    // rememberAgentTurn.  Keep the server's final answer in the client-side
    // context as a real assistant turn instead of silently losing it.
    recentMessages.splice(lastUserIndex + 1, 0, { role: "assistant", content: answer });
  }
  agentMemory.value.recentMessages = recentMessages.filter((message) => message.content.trim()).slice(-8);
  return true;
}

/** 执行器事件 → MapAgentWidget 命令进度 */
function handleExecutorEvent(event: QueueEvent): void {
  if (event.type !== "commandProgress" && event.type !== "commandRetry" && event.type !== "commandDegraded") {
    return;
  }
  if (!event.commandId) return;
  const item: CommandProgressItem = {
    commandId: event.commandId,
    commandType: event.commandType || "",
    status: event.status || "running",
    summary: event.summary || ""
  };
  const index = agentCommandProgress.value.findIndex((entry) => entry.commandId === item.commandId);
  if (index >= 0) {
    const next = [...agentCommandProgress.value];
    next[index] = item;
    agentCommandProgress.value = next;
  } else {
    agentCommandProgress.value = [...agentCommandProgress.value, item];
  }
}

onMounted(async () => {
  void auth.restore();
  // 欢迎页首帧优先；随后在遮罩之下预热默认主页面和图谱数据。
  scheduleWelcomeGraphPreload();
  scheduleWelcomeMainPreload();
  if (!initialHistoryLoadingVisible.value) {
    removeInitialHistoryBootLoader();
    return;
  }
  await router.isReady();
  await nextTick();
  initialPageReady.value = true;
  finishInitialHistoryLoading();
  initialHistoryFallbackTimer = window.setTimeout(() => {
    resolveInitialHistoryImage();
  }, 8000);
});

onBeforeUnmount(() => {
  if (initialHistoryDurationTimer !== undefined) window.clearTimeout(initialHistoryDurationTimer);
  if (initialHistoryFallbackTimer !== undefined) window.clearTimeout(initialHistoryFallbackTimer);
  if (historyScrollAttentionTimer !== undefined) window.clearTimeout(historyScrollAttentionTimer);
  if (welcomeGraphPreloadTimer !== undefined) window.clearTimeout(welcomeGraphPreloadTimer);
  if (welcomeMainPreloadTimer !== undefined) window.clearTimeout(welcomeMainPreloadTimer);
  if (welcomeGraphPreloadIdleId !== undefined) {
    (window as WelcomeIdleWindow).cancelIdleCallback?.(welcomeGraphPreloadIdleId);
  }
  agentController?.abort();
  finalAnswerController?.abort();
  agentExecutor.cancelCurrentTurn("app_unmount");
});
</script>

<template>
  <div
    class="app-container"
    :class="{
      'is-old-photo-module': isOldPhotoModule,
      'mobile-nav-visible': showMobilePrimaryNav
    }"
  >
    <AppHeader
      v-if="showGlobalHeader"
      :active-page="activePage"
      :current-user="auth.currentUser.value"
      :force-nebula-style="activePage === 'map' && mapNebulaMode"
      :scroll-attention="historyScrollEntryAttention"
      @brand="handleBrandAction"
      @navigate="handleNavigate"
      @login="auth.openAuthPanel"
      @share="shareOpen = true"
      @open-scroll="historyScrollViewerOpen = true"
    />
    <MobilePrimaryNav
      v-if="showMobilePrimaryNav"
      :active-page="activePage"
      @navigate="handleNavigate"
    />

    <div
      v-if="!isGrowthRoute && (hasVisitedNebula || welcomeMainPreloadReady)"
      class="layer layer-nebula"
      :class="{ 'is-active': route.path === '/' }"
    >
      <CityNebulaView
        ref="nebulaViewRef"
        :active="route.path === '/'"
        @initial-ready="handleInitialNebulaReady"
      />
    </div>

    <div
      v-if="!isGrowthRoute"
      class="layer layer-map"
      :class="{ 'is-active': route.path === '/map' }"
    >
      <HeritageMapView
        ref="mapViewRef"
        :suppress-initial-loader="initialWelcomeVisible || initialHistoryLoadingVisible"
        @initial-ready="handleInitialMapReady"
      />
    </div>

    <Transition name="page">
      <BuildingDetailView v-if="route.name === 'building-detail'" />
    </Transition>

    <Transition name="page">
      <OldPhotoHomeView v-if="route.name === 'old-photos'" />
    </Transition>

    <Transition name="page">
      <PhotoArchiveSystemView v-if="route.name === 'photo-archive'" />
    </Transition>

    <KeepAlive>
      <PhotoWorkbenchView v-if="route.name === 'photo-workbench'" />
    </KeepAlive>

    <Transition name="page">
      <MyImagesView v-if="route.name === 'my-images'" />
    </Transition>

    <Transition name="page">
      <PhotoAlignmentAdminView v-if="route.name === 'photo-alignment-admin'" />
    </Transition>

    <Transition name="page">
      <PhotoPoseAnnotationView v-if="route.name === 'photo-pose-annotation'" />
    </Transition>

    <Transition name="page">
      <MemoryGridInsightView v-if="route.name === 'memory-grid-insight'" />
    </Transition>

    <Transition name="page">
      <SpatialStatsView v-if="route.name === 'spatial-stats'" />
    </Transition>

    <Transition name="page">
      <CrowdSourceHome v-if="route.name === 'crowdsource'" />
    </Transition>

    <Transition name="page">
      <CrowdSourceSubmitView v-if="route.name === 'crowdsource-submit'" />
    </Transition>

    <Transition name="page">
      <ContributionBrowseView v-if="route.name === 'contribution-browse'" />
    </Transition>

    <Transition name="page">
      <CrowdMapView v-if="route.name === 'crowd-map'" />
    </Transition>

    <Transition name="page">
      <ArchiveReviewView v-if="route.name === 'archive-review'" />
    </Transition>

    <Transition name="page">
      <ArchiveTopicWorkbenchView v-if="route.name === 'archive-topic-workbench'" />
    </Transition>

    <Transition name="page">
      <ContributionDetailView v-if="route.name === 'contribution-detail'" />
    </Transition>

    <Transition name="page">
      <UrbanGrowthView v-if="route.name === 'growth'" />
    </Transition>

    <Transition name="page">
      <UrbanGrowthS3MTestView v-if="route.name === 'growth-s3m-test'" />
    </Transition>

    <AuthDialog :open="auth.authPanelOpen.value" @close="auth.closeAuthPanel" />
    <ShareDialog :open="shareOpen" @close="shareOpen = false" />
    <MapAgentWidget
      ref="agentWidgetRef"
      :response="agentResponse"
      :welcome-context="agentWelcomeContext"
      :loading="agentLoading"
      :error-message="agentError"
      :command-progress="agentCommandProgress"
      :final-answer="agentFinalAnswer"
      :interim-answer="agentInterimAnswer"
      :v4-process-steps="agentV4ProcessSteps"
      :executing="agentExecuting"
      :live-steps="agentLiveSteps"
      @submit="handleAgentSubmit"
      @cancel="handleAgentCancel"
      @reset="handleAgentReset"
    />
    <HistoryScrollViewer
      :open="historyScrollViewerOpen"
      @close="historyScrollViewerOpen = false"
    />
    <InitialHistoryLoading
      :visible="initialHistoryLoadingVisible"
      :closing="initialHistoryClosing"
      @loaded="resolveInitialHistoryImage"
      @error="resolveInitialHistoryImage"
      @closed="handleInitialHistoryClosed"
    />
    <InitialWelcomeGuide
      :visible="initialWelcomeVisible"
      @close="closeInitialWelcome"
      @action="handleInitialWelcomeAction"
      @assistant="openGuideAgent"
    />
  </div>
</template>

<style>
.app-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #07130f;
}
.layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.layer.is-active {
  pointer-events: auto;
}
.layer-nebula {
  z-index: 10;
}
.layer-map {
  z-index: 20;
  opacity: 0;
  visibility: hidden;
  transition: opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1), visibility 1.2s step-end;
}
.layer-map.is-active {
  opacity: 1;
  visibility: visible;
  transition: opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1), visibility 0s;
}
</style>
